import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { 
  ShoppingCart, MessageSquare, Users, ArrowUpRight, Clock, Loader2, TrendingUp 
} from 'lucide-react';
import { 
  AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer 
} from 'recharts';

const Dashboard = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchStats = async () => {
      try {
        const token = localStorage.getItem('adminToken');
        const res = await axios.get(`${process.env.REACT_APP_API_URL}/api/adminworkspace001marvelsato/marvel-sato-management/stats`, {
          headers: { authorization: `Bearer ${token}` }
        });
        setData(res.data);
      } catch (err) {
        console.error("Error fetching stats", err);
      } finally {
        setLoading(false);
      }
    };
    fetchStats();
  }, []);

  if (loading) return (
    <div className="h-96 flex items-center justify-center">
      <Loader2 className="animate-spin text-orange-500" size={40} />
    </div>
  );

  const statsCards = [
    { title: 'Total Orders', value: data?.stats.totalOrders, icon: <ShoppingCart className="text-blue-600" />, bg: 'bg-blue-50' },
    { title: "Today's Orders", value: data?.stats.todayOrders, icon: <Clock className="text-orange-600" />, bg: 'bg-orange-50' },
    { title: 'Total Inquiry', value: data?.stats.totalInquiry, icon: <Users className="text-emerald-600" />, bg: 'bg-emerald-50' },
    { title: 'Total Messages', value: data?.stats.totalMessages, icon: <MessageSquare className="text-purple-600" />, bg: 'bg-purple-50' },
  ];

  return (
    <div className="space-y-8 p-2">
      <div>
        <h1 className="text-2xl font-black text-slate-900 uppercase italic">
          System <span className="text-orange-500">Overview</span>
        </h1>
        <p className="text-slate-500 text-sm font-medium">Real-time performance metrics for Marvel Sato.</p>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {statsCards.map((stat, i) => (
          <div key={i} className="bg-white p-6 rounded-3xl border border-slate-100 shadow-sm hover:shadow-md transition-all">
            <div className="flex items-center justify-between mb-4">
              <div className={`${stat.bg} p-3 rounded-2xl`}>{stat.icon}</div>
              <div className="text-[10px] font-bold text-emerald-600 bg-emerald-50 px-2 py-1 rounded-lg flex items-center">
                LIVE <div className="ml-1 w-1.5 h-1.5 bg-emerald-500 rounded-full animate-pulse" />
              </div>
            </div>
            <p className="text-slate-400 text-[10px] font-black uppercase tracking-widest">{stat.title}</p>
            <h3 className="text-3xl font-black text-slate-900 mt-1">{stat.value}</h3>
          </div>
        ))}
      </div>

      {/* Activity Graph */}
      <div className="bg-white rounded-3xl shadow-sm border border-slate-100 p-8">
        <div className="flex items-center justify-between mb-8">
          <div>
            <h3 className="text-lg font-black uppercase italic text-slate-800">Order Analytics</h3>
            <p className="text-xs text-slate-400 font-bold uppercase">Monthly Sales Volume</p>
          </div>
          <TrendingUp className="text-orange-500" size={24} />
        </div>
        
        <div className="h-80 w-full">
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart data={data?.chartData}>
              <defs>
                <linearGradient id="colorOrders" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#f97316" stopOpacity={0.3}/>
                  <stop offset="95%" stopColor="#f97316" stopOpacity={0}/>
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f1f5f9" />
              <XAxis 
                dataKey="name" 
                axisLine={false} 
                tickLine={false} 
                tick={{fill: '#94a3b8', fontSize: 12, fontWeight: 'bold'}} 
                dy={10}
              />
              <YAxis 
                axisLine={false} 
                tickLine={false} 
                tick={{fill: '#94a3b8', fontSize: 12, fontWeight: 'bold'}} 
              />
              <Tooltip 
                contentStyle={{borderRadius: '16px', border: 'none', boxShadow: '0 10px 15px -3px rgb(0 0 0 / 0.1)'}}
              />
              <Area 
                type="monotone" 
                dataKey="orders" 
                stroke="#f97316" 
                strokeWidth={4}
                fillOpacity={1} 
                fill="url(#colorOrders)" 
              />
            </AreaChart>
          </ResponsiveContainer>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;